import { PopulatedDatabaseQuestion, PopulatedDatabaseAnswer } from '../types/types';
import api from './config';
import { getQuestionsByFilter } from './questionService';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

/**
 * Interface representing a user's activity item
 * Can be either a question or an answer with additional metadata
 */
export interface ActivityItem {
  type: 'question' | 'answer';
  date: Date;
  content: PopulatedDatabaseQuestion | PopulatedDatabaseAnswer;
  parentQuestion?: PopulatedDatabaseQuestion; // For answers, the question they belong to
}

/**
 * Function to get questions asked by a specific user
 *
 * @param username - The username to fetch questions for
 */
const getQuestionsByUser = async (username: string): Promise<PopulatedDatabaseQuestion[]> => {
  try {
    // Get all questions with the most recent order
    const allQuestions = await getQuestionsByFilter('newest', '');

    // Filter questions to only return those asked by the specific user
    return allQuestions.filter(
      (question: PopulatedDatabaseQuestion) => question.askedBy === username,
    );
  } catch (error) {
    throw new Error('Error when fetching questions by user');
  }
};

/**
 * Function to get answers provided by a specific user
 *
 * @param username - The username to fetch answers for
 */
const getAnswersByUser = async (
  username: string,
): Promise<{ answer: PopulatedDatabaseAnswer; question: PopulatedDatabaseQuestion }[]> => {
  const res = await api.get(`${SERVER_URL}/answer/getAnswersByUser/${username}`);
  if (res.status !== 200) {
    throw new Error('Error when fetching answers by user');
  }
  return res.data;
};

/**
 * Function to get all activity (questions and answers) for a specific user
 *
 * @param username - The username to fetch activity for
 */
const getUserActivity = async (username: string): Promise<ActivityItem[]> => {
  // Fetch both questions and answers
  const questions = await getQuestionsByUser(username);
  const answersWithQuestions = await getAnswersByUser(username);

  // Format questions as activity items
  const questionActivity: ActivityItem[] = questions.map(question => ({
    type: 'question',
    date: new Date(question.askDateTime),
    content: question,
  }));

  // Format answers as activity items
  const answerActivity: ActivityItem[] = answersWithQuestions.map(({ answer, question }) => ({
    type: 'answer',
    date: new Date(answer.ansDateTime),
    content: answer,
    parentQuestion: question,
  }));

  // Combine and sort by date (newest first)
  const allActivity = [...questionActivity, ...answerActivity];
  return allActivity.sort((a, b) => b.date.getTime() - a.date.getTime());
};

/**
 * Function to get activity feed for a user including their own activity
 * and activity from users they follow
 *
 * @param username - The current user's username
 * @param includeSelf - Whether to include the user's own activity (default: true)
 * @param includeFollowing - Whether to include activity from followed users (default: true)
 */
const getActivityFeed = async (
  username: string,
  includeSelf: boolean = true,
  includeFollowing: boolean = true,
): Promise<ActivityItem[]> => {
  let allActivity: ActivityItem[] = [];

  // Get the user's own activity if requested
  if (includeSelf) {
    const userActivity = await getUserActivity(username);
    allActivity = [...allActivity, ...userActivity];
  }

  // Get activity from users they follow if requested
  if (includeFollowing) {
    const res = await api.get(`${SERVER_URL}/user/${username}`);
    if (res.status !== 200) {
      throw new Error('Error fetching user details');
    }

    const userData = res.data;
    // Convert following from object to Map if it's not already
    const following =
      userData.following instanceof Map
        ? userData.following
        : new Map(Object.entries(userData.following || {}));

    // Get activity for each followed user
    const followingPromises: Promise<ActivityItem[]>[] = [];
    following.forEach((_: unknown, followedUser: string) => {
      followingPromises.push(getUserActivity(followedUser));
    });

    const followingActivities = await Promise.all(followingPromises);

    // Flatten the array of activity arrays
    const followingActivity = followingActivities.flat();
    allActivity = [...allActivity, ...followingActivity];
  }

  // Sort all activity by date (newest first)
  return allActivity.sort((a, b) => b.date.getTime() - a.date.getTime());
};

export { getUserActivity, getActivityFeed, getQuestionsByUser, getAnswersByUser };
